import { ListPage } from '@components/molecules';
import { DatePickerElement } from 'react-hook-form-mui/date-pickers';
import {
  Box,
  Button,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from '@mui/material';
import API from '@apps/nnc/services/failuremode';
import { ModelProps } from '@/types';
import { useTranslation } from 'react-i18next';
import { JSX, useEffect, useState } from 'react';
import { NncSchema, NncSearchSchema } from '@apps/nnc/schemas/nnc';
import AsyncAutocompleteElement from '@/components/atoms/asyncautocomplete';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import UnitAPI from '@apps/localization/services/unit';
// import { AutocompleteElement } from 'react-hook-form-mui';
import { GridRenderCellParams, GridRowSelectionModel } from '@mui/x-data-grid';
import { useNavigate } from 'react-router';
import CountryAPI from '@apps/localization/services/country';
import { useMenuContext } from '@/contexts/menu';
import { AutocompleteElement } from 'react-hook-form-mui';

export default function EntriesListPage(modelProps: ModelProps): JSX.Element {
  const { t } = useTranslation();
  const defaultFormContext = useForm<z.infer<z.ZodTypeAny>>({
    defaultValues: {
      status: {
        id: 1,
        label: t('Pending'),
      },
    },
  });
  // const start = defaultFormContext.watch('start_date', null);
  // const end = defaultFormContext.watch('end_date', null);
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [selectedUnitId, setSelectedUnitId] = useState<number | null>(null);
  const [rows, setRows] = useState<unknown[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [justification, setJustification] = useState('');
  const [formEffect, setFormEffect] = useState(false);
  const { openMenu } = useMenuContext();
  const navigate = useNavigate();

  const handleCreate = () => {
    navigate(`/nnc/failuremode/add-update/${selection.join('-')}`);
  };

  const opt = [
    {
      id: 1,
      label: t('Pending'),
    },
    {
      id: 2,
      label: t('Assigned'),
    },
    {
      id: 3,
      label: t('Unfounded'),
    },
  ];

  const columns = [
    {
      field: 'division',
      headerName: t('Identified Area'),
      width: 160,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const machines = params.row.other_divisions || [];
        return (
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={1}
            sx={{ height: '100%' }}
          >
            {params.row.main_division || ''}
            {machines.length > 0 && (
              <Tooltip
                title={
                  <>
                    {machines.map((machine: string, index: number) => (
                      <Box key={index} sx={{ marginBottom: '4px' }}>
                        {machine}
                      </Box>
                    ))}
                  </>
                }
              >
                <Box
                  sx={{
                    background: '#00d160',
                    padding: '0 5px',
                    borderRadius: '4px',
                    color: '#fff',
                    minWidth: '30px',
                    display: 'flex',
                    height: '30px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  +{machines.length || 0}
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'machine',
      headerName: t('NNC Machine'),
      flex: 2,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const machines = params.row.other_machine || [];
        return (
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={1}
            sx={{ height: '100%' }}
          >
            {params.row.main_machine || ''}
            {machines.length > 0 && (
              <Tooltip
                title={
                  <>
                    {machines.map((machine: string, index: number) => (
                      <Box key={index} sx={{ marginBottom: '4px' }}>
                        {machine}
                      </Box>
                    ))}
                  </>
                }
              >
                <Box
                  sx={{
                    background: '#00d160',
                    padding: '0 5px',
                    borderRadius: '4px',
                    color: '#fff',
                    minWidth: '30px',
                    display: 'flex',
                    height: '30px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  +{machines.length || 0}
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'operator',
      headerName: t('Operator Generator'),
      flex: 2,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const machines = params.row.other_operators || [];
        return (
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={1}
            sx={{ height: '100%' }}
          >
            {params.row.main_operator || ''}
            {machines.length > 0 && (
              <Tooltip
                title={
                  <>
                    {machines.map((machine: string, index: number) => (
                      <Box key={index} sx={{ marginBottom: '4px' }}>
                        {machine}
                      </Box>
                    ))}
                  </>
                }
              >
                <Box
                  sx={{
                    background: '#00d160',
                    padding: '0 5px',
                    borderRadius: '4px',
                    color: '#fff',
                    minWidth: '30px',
                    display: 'flex',
                    height: '30px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  +{machines.length || 0}
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'origin',
      headerName: t('Origin'),
      renderCell: (params: GridRenderCellParams) => {
        return params?.row?.origin?.label;
      },
      width: 120,
    },
    {
      field: 'cause',
      headerName: t('Cause'),
      renderCell: (params: GridRenderCellParams) => {
        return params?.row?.cause?.label;
      },
      width: 120,
    },
    {
      field: 'comment',
      headerName: t('Comment'),
      flex: 2,
    },
    {
      field: 'status',
      headerName: t('Status'),
      renderCell: (params: GridRenderCellParams) => {
        if (params?.row?.unfounded) {
          return t('Unfounded');
        }
        return params?.row?.status?.label;
      },
      width: 150,
    },
    {
      field: 'analysis_reference_number',
      headerName: t('Analysis'),

      width: 150,
    },
  ];

  useEffect(() => {
    if (selection.length === 0) {
      setSelectedUnitId(null);
    } else if (selection.length > 0 && selectedUnitId === null && rows.length > 0) {
      const firstSelectedRow = rows.find((row: unknown) => {
        const r = row as { id: number; unit_id: number };
        return selection.includes(r.id);
      }) as { unit_id: number } | undefined;
      if (firstSelectedRow) {
        setSelectedUnitId(firstSelectedRow.unit_id);
      }
    }
  }, [selection, rows, selectedUnitId]);

  return (
    <>
      <Box sx={{ position: 'relative', pb: 12 }}>
        <ListPage
          formcontext={defaultFormContext}
          schema={NncSchema}
          schemaSearch={NncSearchSchema}
          title={t('Fail Mode Entries')}
          customPath={'nnc/failmode'}
          api={API}
          columns={columns}
          modelProps={modelProps}
          checkboxSelection
          rowSelectionModel={selection}
          setRowSelectionModel={setSelection}
          disableAdd
          disableDelete
          disableEdit
          effect={formEffect}
          isRowSelectable={(params) => {
            const status = params.row.status?.id;
            const unit_id = params.row.unit_id;
            
            if (status !== 1) return false;
            
            if (selectedUnitId === null) return true;
            
            return unit_id === selectedUnitId;
          }}
          onRowsChange={setRows}
        >
          <Grid size={{ xs: 12, md: 1.5 }}>
            <DatePickerElement
              name="start_date"
              label={t('Start Date')}
              inputProps={{ sx: { width: '100%' } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 1.5 }}>
            <DatePickerElement
              name="end_date"
              label={t('End Date')}
              inputProps={{ sx: { width: '100%' } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2.5 }}>
            <AsyncAutocompleteElement
              api={CountryAPI.select}
              name="country"
              label={t('Country')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2.5 }}>
            <AsyncAutocompleteElement
              api={UnitAPI.select}
              name="unit"
              label={t('Unit')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2.8 }}>
            <AutocompleteElement
              name="status"
              label={t('Status')}
              options={opt}
            />
          </Grid>
          {/* <Grid size={{ xs: 12, md: 5 }}>
        <AutocompleteElement
          name="status"
          label={t('Status')}
          multiple
          options={opt}
        />
      </Grid> */}

          {selection.length > 0 ? (
            <Box
              sx={{
                position: 'fixed',
                bottom: 30,
                width: openMenu
                  ? 'calc(100vw - 240px - 48px)'
                  : 'calc(100vw - 48px)',
                padding: 2,
                background: '#FFF',
                zIndex: 1,
                boxShadow: '4px 4px 4px #00000050',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                marginLeft: '-16px',
              }}
            >
              <Typography>{t('NNC Evaluation')}</Typography>
              <Box display={'flex'} gap={2}>
                <Button
                  sx={{ background: '#191B27' }}
                  onClick={() => setOpenModal(true)}
                >
                  {t('Dismiss Action Plan')}
                </Button>
                <Button onClick={() => handleCreate()}>
                  {t('Create Action Plan')}
                </Button>
              </Box>
            </Box>
          ) : (
            <></>
          )}
        </ListPage>
      </Box>

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        slotProps={{
          paper: { sx: { borderRadius: '16px !important', maxWidth: '700px' } },
        }}
      >
        <DialogTitle
          justifyContent={'space-between'}
          display="flex"
          alignItems="center"
        >
          {t('Justification')}
          <Button
            color="inherit"
            variant="text"
            onClick={() => setOpenModal(false)}
          >
            X
          </Button>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('Describe Justification')}
            type="text"
            fullWidth
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            sx={{ width: '600px' }}
            multiline
            rows={4}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              await API.unfounded(selection, justification);
              setOpenModal(false);
              setFormEffect(!formEffect);
              setJustification('');
            }}
            disabled={!justification}
            variant="contained"
            color="primary"
          >
            {t('Save and Close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
